import React, { memo, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import _debounce from "lodash/debounce";
import {
  RangeDropdown,
  ExcelBtn,
  SearchBar,
  Pagination,
  NoRecordFound,
} from "../../component/commonComponent";
import doubleArrow from "../../assets/admin Images/2arrows.svg";
import { PageSize } from "../../utils/constant";
import { exportToCSV, getToken, toastMessage } from "../../utils/helper";
import userAuth from "../../utils/api/userAuth";
import moment from "moment";
import PlatformSurveyFilterBtn from "../../component/commonComponent/PlatformSurveyFilterBtn";
import TIMER_ICON from "../../assets/images/clock-filter.svg";
import axios from "axios";
import { Info } from "lucide-react";

const mapNpsResponseToSurveyRow = (item) => {
  const { surveyMonth, surveyYear } = item || {};

  let monthLabel = "";
  if (surveyMonth && surveyYear) {
    monthLabel = moment(`${surveyYear}-${surveyMonth}-01`, "YYYY-M-D").format(
      "MMM YYYY",
    );
  }

  return {
    employerId: item?.employerCode || "",
    employerName: item?.employerName || "",
    month: monthLabel,
    product: item?.productName || "",
    recommendationScore: item?.recommendationScore ?? 0,
    recommendationComment: item?.recommendationComment || "",
    npsCategory: item?.category || "",
    serviceSatisfaction: item?.serviceScore ?? 0,
    serviceComment: item?.serviceComment || "",
    platformSatisfaction: item?.platformScore ?? 0,
    platformComment: item?.platformComment || "",
    suggestions: item?.comment || "",
    submittedBy: item?.userName || "",
    submittedOn: item?.submittedAt || "",
  };
};

const headerOptions = [
  {
    name: "Employer Name",
    sortBy: "employerName",
    width: "w-12",
  },
  {
    name: "Employer Name",
    sortBy: "employerName",
    width: "w-12",
  },
  {
    name: "Month",
    sortBy: "month",
    width: "w-11",
  },
  {
    name: "Product",
    sortBy: "",
    width: "w-12",
  },
  {
    name: "Recommendation Score",
    sortBy: "",
    width: "w-15",
  },
  {
    name: "NPS Category",
    sortBy: "npsCategory",
    width: "w-13",
  },
  {
    name: "Service Satisfaction",
    sortBy: "",
    width: "w-14",
  },
  {
    name: "Platform Satisfaction",
    sortBy: "",
    width: "w-14",
  },
  {
    name: "Suggestions / Remarks",
    sortBy: "",
    width: "w-30",
  },
  {
    name: "Submitted by",
    sortBy: "",
    width: "w-12",
  },
  {
    name: "Submitted on",
    sortBy: "",
    width: "w-16",
  },
];

const defaultFilters = {
  from: "",
  to: "",
  user: "",
  employer: "",
  rating: "",
  category: "",
  productId: "",
};

const getTrimmedText = (text = "", maxLength = 30) => {
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const tooltipStyle = {
  maxWidth: "260px",
  whiteSpace: "normal",
  wordWrap: "break-word",
  userSelect: "text",
  zIndex: 999999,
  padding: "8px",
};

const PlatformTable = memo(() => {
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesLimit, setEntriesLimit] = useState(PageSize);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [isAsc, setIsAsc] = useState(true);
  const [surveyData, setSurveyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFiltersState] = useState({ ...defaultFilters });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const handleEntriesLimit = (limit) => {
    setEntriesLimit(limit);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleChangeSort = (key) => {
    if (sortBy === key) {
      setIsAsc(!isAsc);
    } else {
      setSortBy(key);
      setIsAsc(true);
    }
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const applyFilters = (nextFilters) => {
    setCurrentPage(1);
    const merged = { ...defaultFilters, ...nextFilters };

    if (!merged.user && merged.employer) {
      merged.user = merged.employer;
    }

    setFiltersState(merged);
  };

  const filtersForControl = useMemo(() => {
    return {
      from: filters.from ? moment(filters.from).format("YYYY-MM-DD") : "",
      to: filters.to ? moment(filters.to).format("YYYY-MM-DD") : "",
      employer: filters.employer || "",
      employerUserId: filters.user || "",
      rating:
        filters.rating === null || filters.rating === undefined
          ? ""
          : String(filters.rating),
      category: filters.category || "",
      productId: filters.productId || "",
    };
  }, [filters]);

  useEffect(() => {
    const fetchPlatformSurvey = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(currentPage));
        params.set("pageSize", String(entriesLimit));

        if (filters.from) {
          params.set("from", filters.from);
        }

        if (filters.to) {
          params.set("to", filters.to);
        }

        if (filters.user) {
          params.set("user", filters.user);
        }

        const normalizedRating =
          filters.rating === null ||
          filters.rating === undefined ||
          String(filters.rating).trim() === ""
            ? ""
            : String(filters.rating).trim();

        if (normalizedRating) {
          params.set("rating", normalizedRating);
        }

        if (filters.category && String(filters.category).trim()) {
          params.set("category", String(filters.category).trim());
        }

        if (filters.productId && String(filters.productId).trim()) {
          params.set("productId", String(filters.productId).trim());
        }

        if (search.trim()) {
          params.set("search", search.trim());
        }

        if (sortBy) {
          const direction = isAsc ? "asc" : "desc";
          params.set("sort", `${sortBy} ${direction}`);
        }

        const queryString = params
          .toString()
          .replace(/%3A/gi, ":")
          .replace(/%2C/gi, ",");
        const endpoint = queryString
          ? `/admin/users/nps/responses?${queryString}`
          : "/admin/users/nps/responses";

        const response = await userAuth.get(endpoint);
        // const response = await axios.get(`http://172.16.2.9:4001/api/v1/admin/users/nps/responses?${queryString}`,{
        //     headers: {
        //         Authorization: `Bearer ${getToken()}`,
        //     },
        // });
        const apiData = response?.data?.result?.data || [];
        const mappedData = apiData.map(mapNpsResponseToSurveyRow);
        setSurveyData(mappedData);
        // Extract total count from API response - check multiple possible locations
        const total =
          response?.data?.result?.total ||
          response?.data?.result?.count ||
          response?.data?.total ||
          mappedData.length;
        setTotalCount(total);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to load platform survey data.";
        toastMessage("error", message);
        setSurveyData([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlatformSurvey();
  }, [
    currentPage,
    entriesLimit,
    filters.from,
    filters.to,
    filters.user,
    filters.rating,
    filters.category,
    filters.productId,
    search,
    sortBy,
    isAsc,
    refreshTrigger,
  ]);

  // API handles filtering, sorting, and pagination server-side
  // So we use surveyData directly without client-side processing

  const handleRefresh = _debounce(() => {
    setSortBy("");
    setSearch("");
    setCurrentPage(1);
    applyFilters(defaultFilters);
    setRefreshTrigger((prev) => prev + 1);
  }, 300);

  const surveyDataForCsv = useMemo(() => {
    return surveyData.map((item) => ({
      "Employer ID": item.employerId,
      "Employer Name": item.employerName,
      Month: item.month,
      Product: item.product,
      "Recommendation Score": `${item.recommendationScore}/10`,
      "NPS Category": item.npsCategory,
      "Service Satisfaction": `${item.serviceSatisfaction}.0/5`,
      "Platform Satisfaction": `${item.platformSatisfaction}.0/5`,
      "Suggestions / Remarks": item.suggestions,
      "Submitted by": item.submittedBy,
      "Submitted on - Date": moment(item.submittedOn).format("Do MMM, YYYY"),
      "Submitted on - Time": moment(item.submittedOn).format("h:mm A"),
    }));
  }, [surveyData]);

  return (
    <>
      <div className="page-container eep">
        <div className="mpl-title-container d-flex justify-content-between align-items-center rounded-3">
          <div className="text-theme-blue f-sem-bol f-20">
            Platform Survey {`(${totalCount})`}
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between">
          <RangeDropdown handleEntriesLimit={handleEntriesLimit} />
          <div className="d-flex align-items-center">
            <SearchBar
              onChange={handleSearch}
              value={search}
              containerClass={"search-bar-container-type1"}
              inputClass={"search-input-type1"}
            />
            <img
              src={TIMER_ICON}
              alt="timer"
              className="refresh-btn"
              onClick={handleRefresh}
            />
            <PlatformSurveyFilterBtn
              setFilters={applyFilters}
              activeFilters={filtersForControl}
              showNpsCategory={true}
            />
            {surveyData.length > 0 ? (
              <ExcelBtn
                onClick={_debounce(
                  () => exportToCSV(surveyDataForCsv, "Platform Survey List"),
                  300,
                )}
              />
            ) : (
              <ExcelBtn excelBtnClassName="excel-grey-btn cursor-na" />
            )}
          </div>
        </div>
        <div className="sp-list">
          <div className="header d-flex f-12 f-sem-bol rounded-3">
            {headerOptions.map((data, index) => (
              <div key={index} className={`header-content ${data.width}`}>
                <span>{data.name}</span>
                {data.sortBy && (
                  <img
                    src={doubleArrow}
                    alt="double"
                    className="img-fluid"
                    onClick={() => handleChangeSort(data.sortBy)}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="row-container">
            {isLoading ? (
              <div className="text-center py-3">Loading...</div>
            ) : surveyData.length > 0 ? (
              surveyData.map((item, index) => (
                <div
                  className="support-row d-flex f-12 f-sem-bol rounded-3"
                  key={index}
                >
                  <div className="row-content w-12">
                    <span className="t-clip w-100 text-center">
                      {item.employerName}
                    </span>
                  </div>
                  <div className="row-content w-12">
                    <span className="t-clip w-100 text-center">
                      {item.employerName}
                    </span>
                  </div>
                  <div className="row-content w-11">
                    <span className="t-clip w-100 text-center">
                      {item.month}
                    </span>
                  </div>
                  <div className="row-content w-12">
                    <span className="t-clip w-100 text-center">
                      {item.product}
                    </span>
                  </div>
                  <div className="row-content w-15">
                    <span className="t-clip w-100 text-center service-satisfaction-container">
                      {item.recommendationScore}/10
                      {item?.recommendationComment && (
                        <>
                          <Info
                            size={12}
                            data-tooltip-id={`recommendation-comment-tooltip-${index}`}
                            data-tooltip-content={item.recommendationComment}
                            data-tooltip-place="top"
                            style={{ cursor: "pointer" }}
                          />
                          <Tooltip
                            id={`recommendation-comment-tooltip-${index}`}
                            className="tooltip-custom"
                            positionStrategy="fixed"
                            style={tooltipStyle}
                          />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="row-content w-13">
                    <span className="t-clip w-100 text-center text-success">
                      {item.npsCategory}
                    </span>
                  </div>
                  <div className="row-content w-14">
                    <span className="t-clip w-100 text-center service-satisfaction-container">
                      {item.serviceSatisfaction}.0/5
                      {item?.serviceComment && (
                        <>
                          <Info
                            size={12}
                            data-tooltip-id={`service-comment-tooltip-${index}`}
                            data-tooltip-content={item.serviceComment}
                            data-tooltip-place="top"
                            style={{ cursor: "pointer" }}
                          />
                          <Tooltip
                            id={`service-comment-tooltip-${index}`}
                            className="tooltip-custom"
                            positionStrategy="fixed"
                            style={tooltipStyle}
                          />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="row-content w-14">
                    <span className="t-clip w-100 text-center service-satisfaction-container">
                      {item.platformSatisfaction}.0/5
                      {item?.platformComment && (
                        <>
                          <Info
                            size={12}
                            data-tooltip-id={`platform-comment-tooltip-${index}`}
                            data-tooltip-content={item.platformComment}
                            data-tooltip-place="top"
                            style={{ cursor: "pointer" }}
                          />
                          <Tooltip
                            id={`platform-comment-tooltip-${index}`}
                            className="tooltip-custom"
                            positionStrategy="fixed"
                            style={tooltipStyle}
                          />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="row-content w-30">
                    <span
                      className="t-clip w-100 text-center"
                      data-tooltip-id={`suggestions-tooltip-${index}`}
                      data-tooltip-content={item.suggestions}
                      data-tooltip-place="top"
                    >
                      {getTrimmedText(item.suggestions)}
                    </span>
                    <Tooltip
                      id={`suggestions-tooltip-${index}`}
                      className="tooltip-custom"
                      positionStrategy="fixed"
                      style={tooltipStyle}
                    />
                  </div>
                  <div className="row-content w-12">
                    <span
                      className="t-clip w-100 text-center"
                      data-tooltip-id={`submitted-by-tooltip-${index}`}
                      data-tooltip-content={item.submittedBy}
                      data-tooltip-place="top"
                    >
                      {item.submittedBy}
                    </span>
                    <Tooltip
                      id={`submitted-by-tooltip-${index}`}
                      className="tooltip-custom"
                      positionStrategy="fixed"
                      style={tooltipStyle}
                    />
                  </div>
                  <div className="row-content w-16">
                    <span className="t-clip w-100 text-center">
                      {moment(item.submittedOn).format("Do MMM, YYYY")}
                      <br />
                      {moment(item.submittedOn).format("h:mm A")}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <NoRecordFound />
            )}
          </div>
        </div>
        {surveyData.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={entriesLimit}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </>
  );
});

export default PlatformTable;
